import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toggleSideBar } from "../../redux/header/headerAction";
import { filterNavigationItems } from "../../common/permissionController";
import DashboardIcon from "../../assets/images/dashboard-icon.svg";
import UsersIcon from "../../assets/images/users.svg";
import organisationIcon from "../../assets/images/organization-chart.svg";
import salarystructure from "../../assets/images/salarystructure.svg";
import attenance from "../../assets/images/attendance.png";
import calendar from "../../assets/images/calendar.svg";
import kpi from "../../assets/images/indicator.svg";
import assets from "../../assets/images/briefcase-2-line.svg";
import leave from "../../assets/images/calendar-event-line.svg";
import announcements from "../../assets/images/announcement.svg";
import { ReadOutlined, ApartmentOutlined, ScheduleOutlined } from "@ant-design/icons";
import moment from 'moment';
import { RosterApiResponse, UserIdState } from "../../pages/Roster/types";
import { OrganisationIdState } from "../../pages/announcement/layouts/mailList/mailListsInterface";

import "./Sidebar.scss";
import { triggerNotification, useGetApiRequests } from "../../common/CommonExport";
import { DocumentType, DocumentCode, RootState, NavigationItem, RolePermission } from "./types";



export default function Sidebar() {
    const location = useLocation();
    const employeeDetails = useSelector((state: RootState) => state?.employee?.employeeDetails);

    const id = employeeDetails?.employeeId;
    const navigate = useNavigate();
    const rolePermissions = useSelector((state: RootState) => state?.rolePermissions?.rolePermissions);
    const roleName = useSelector((state: RootState) => state?.loginUserData?.userData?.data?.role?.name);
    const allowedRoles = ['IT Admin', 'Reporting Manager'];
    const showOrgSidebar = allowedRoles.includes(roleName);
    const isReportingManger = roleName === 'Reporting Manager';
    const showKpi = rolePermissions?.some(
        (permission: RolePermission) => permission?.component?.name === "KPI" && permission?.isView
    );



    const employeePermission = useSelector((state: RootState) => state?.employeePermissionSlice?.employeePermission);
    const isSidebarOpen = useSelector((state: RootState) => state.toggleSideBar.data);
    const [isToggle, setIsToggle] = useState<boolean>();
    const dispatch = useDispatch();
    const [sideBarList, setSideBarList] = useState<NavigationItem[]>([]);
    const [documentTypeDetails, setDocumentTypeDetails] = useState<DocumentType[]>([]);
    const [rosterData, setRosterData] = useState<RosterApiResponse['data'] | null>(null);


    const orgId = useSelector((state: OrganisationIdState) => state?.organisationId?.id);
    const reportingManagerId = useSelector((state: UserIdState) => state?.userId?.id);
    const getRosterPlan = useGetApiRequests('getRosterPlan', 'GET');
    const memoizedGetRosterPlan = useMemo(() => getRosterPlan, []);

    const baseUrl = process.env.REACT_APP_GOOGLR_DOCUMENT_VIEW;

    const reduxToken = useSelector((state: RootState) => state?.authToken?.token);
    const employeeDocumentList = useGetApiRequests("employeeDocumentStatus", "GET");
    const navigationItems: NavigationItem[] = [
        { name: "Dashboard", path: "dashboard", permission: "Dashboard", icon: DashboardIcon },
        { name: "Users", path: "users", permission: "User", icon: UsersIcon },
        { name: "Organization", path: "organisation", permission: "Organisation", icon: organisationIcon },
        ...(showKpi ? [{ name: "KPI", path: "dashboard/kpi", permission: "KPI", icon: kpi }] : []),
        { name: "Asset Master", path: "asset-master", icon: assets },
        { name: "Salary Structure", path: "salary-master", permission: "Salary Structure", icon: salarystructure },
        { name: "Leave management", path: "dashboard/leaveMangement", permission: "", icon: leave },
        // { name: 'Learning management', path: 'dashboard/learning-management', permission: '', icon: graduation },
        { name: "Holiday", path: "dashboard/holiday", permission: "", icon: calendar },
        { name: "Attendance", path: "dashboard/attanance", permission: "", icon: attenance },
        { name: "Announcement", path: "announcements/all", permission: "", icon: announcements },
        ...(showOrgSidebar ? [{
            name: "Organization Chart",
            path: "organization-chart",
            permission: "Organisation Chart",
            icon: ApartmentOutlined,
        }] : []),
        ...(isReportingManger && rosterData?.status?.message !== "Roster Plan not eligible for this department" ? [{
            name: "Roster Plan",
            path: "roster",
            permission: "",
            icon: ScheduleOutlined,
        }] : [])
    ];

    const employeeNavigationItems: NavigationItem[] = [
        { name: "User Dashboard", path: "user-dashboard", permission: "User Dashboard", icon: DashboardIcon },
        {
            name: "Employee Handbook",
            permission: "User Dashboard",
            icon: ReadOutlined,
        },
        {
            name: "Organization Chart",
            path: "organization-chart",
            permission: "Organisation Chart",
            icon: ApartmentOutlined,
        }
    ];
    const currentUrl = window.location.href;
    const urlParts = currentUrl.split("/");
    const currentUrlName = urlParts[urlParts.length - 1];

    const fetchEmployeeHandbook = async (): Promise<void> => {
        if (!employeePermission || !reduxToken || !id) return;

        const pathParams = { id, end: "documentDetail" };

        try {
            const res = (await employeeDocumentList("", { sessionToken: reduxToken }, pathParams)) as { data: { data: DocumentType[] } };

            const results: DocumentType[] = res?.data?.data || [];
            const filteredResults = results.filter((item) => item.document?.code === DocumentCode.EMPLOYEE_HANDBOOK);
            setDocumentTypeDetails(filteredResults);
        } catch (err) {
            console.error("Failed to fetch employee documents:", err);
        }
    };

    const handleOpenHandbook = () => {
        if (documentTypeDetails.length > 0) {
            const handbook = documentTypeDetails[0];
            const documentPath = handbook?.documentPath;
            if (documentPath && baseUrl) {
                window.open(`${baseUrl}${documentPath}`, "_blank");
            } else {
                triggerNotification("error", "", "No employee handbook found.", "topRight");
            }
        } else {
            triggerNotification("warning", "", "No employee handbook found.", "topRight");
        }
        dispatch(toggleSideBar(false));
    };

    useEffect(() => {
        let filteredNavigationItems = filterNavigationItems(navigationItems, rolePermissions);
        let filteredItemsExcludingAssetMaster = filteredNavigationItems.filter((item: NavigationItem) => item.name !== "Asset Master");
        const finalNavigationItems = employeePermission ? employeeNavigationItems : filteredItemsExcludingAssetMaster;
        setSideBarList(finalNavigationItems);
        setIsToggle(isSidebarOpen);
    }, [isSidebarOpen, employeePermission, rolePermissions, rosterData]);

    useEffect(() => {
        fetchEmployeeHandbook();
    }, [employeePermission, reduxToken, id]);

    useEffect(() => {
        const fetchRosterData = async () => {

            if (isReportingManger && orgId && reportingManagerId) {
                try {
                    const response = await memoizedGetRosterPlan(null, {
                        organisationId: orgId,
                        reportingMangerId: reportingManagerId,
                        start: 0,
                        limit: 10
                    }, {
                        monthYear: moment().format('MM-YYYY')
                    }) as RosterApiResponse;

                    if (response?.data) {
                        setRosterData(response.data);

                    }
                } catch (error) {
                    console.error("Failed to fetch roster plan in sidebar:", error);
                }
            }
        };

        fetchRosterData();
    }, [isReportingManger, orgId, reportingManagerId, memoizedGetRosterPlan]);

    const routesToHideHeader = ["/", "/login", "/forgot-password", "/set-password", "/reset-password", "/home"];
    if (routesToHideHeader.includes(location.pathname)) {
        return null;
    }
    const handleNavigate = (item: NavigationItem) => {
        if (item.name === "Employee Handbook") {
            handleOpenHandbook();
            return;
        }

        const path = item.path;
        if (path === "dashboard") {
            navigate(`/home/dashboard`);
        } else if (path === "user-dashboard") {
            navigate(`/user-dashboard`);
        } else if (path === "user-dashboard/user-holiday") {
            navigate(`/user-dashboard/user-holiday`);
        } else if (path === "user-dashboard/user-attendance") {
            navigate(`/user-dashboard/user-attendance`);
        } else if (path === "organization-chart") {
            navigate(`/organization-chart`)
        } else {
            navigate(`/home/${path}`);
        }
        dispatch(toggleSideBar(false));
    };

    return (
        <div className="sidebar-container">
            {sideBarList?.length > 0 && (
                <div className={isToggle ? "sidebar-container__toggle" : " sidebar-container__toggle__active"}>
                    <ul className="sidebar-container__menu">
                        {sideBarList.map((item: NavigationItem, index: number) => (
                            <li className={`${currentUrlName === item?.path ? "active-li" : ""}`} key={`${item?.name}_${index + 1}`} onClick={() => handleNavigate(item)}>
                                <div className="flex items-center sidebar-icon">
                                    <div className="icon">
                                        {item.name === "Employee Handbook" ? (
                                            <ReadOutlined style={{ fontSize: 18 }} />
                                        ) : item.name === "Organization Chart" ? (
                                            <ApartmentOutlined style={{ fontSize: 18 }} />
                                        ) : item.name === "Roster Plan" ? (
                                            <ScheduleOutlined style={{ fontSize: 18 }} />
                                        ) : (
                                            <img src={item?.icon as string} alt={item?.name} style={{ width: '18px' }} />
                                        )}
                                    </div>
                                    <span className="text-[12px] 2xl:text-[16px]">{item.name}</span>
                                </div>

                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
